import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';
import SEO, { SEO_CONFIG } from '@/components/SEO';
import {
  Sparkles,
  Send,
  Loader2,
  Crown,
  Target,
  Activity,
  Moon,
  Flame,
  ArrowLeft
} from 'lucide-react';
import { Link } from 'react-router-dom';

const API = '/api';

const QUICK_PROMPTS = [
  { text: "Bugün ne yapmalıyım?", icon: Target },
  { text: "Motivasyonum düşük, yardım et", icon: Sparkles },
  { text: "Hedeflerime nasıl ulaşırım?", icon: Activity },
];

const AICoachPage = () => {
  const navigate = useNavigate();
  const [messages, setMessages] = useState([]);
  const [history, setHistory] = useState([]);
  const [inputMessage, setInputMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [loadingHistory, setLoadingHistory] = useState(true);
  const [isPremium, setIsPremium] = useState(false);
  const [remainingMessages, setRemainingMessages] = useState(1);
  const messagesEndRef = useRef(null);

  useEffect(() => {
    fetchHistory();
    checkPremium();
  }, []);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const checkPremium = async () => {
    const token = localStorage.getItem('token');
    if (!token) return;
    try {
      const response = await axios.get(`${API}/auth/me`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setIsPremium(response.data?.is_premium || false);
    } catch (error) {
      console.error('Error:', error);
    }
  };

  const fetchHistory = async () => {
    const token = localStorage.getItem('token');
    if (!token) {
      setLoadingHistory(false);
      return;
    }
    try {
      const response = await axios.get(`${API}/ai-coach/history?limit=10`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const historyData = response.data || [];
      setHistory(historyData);
      // Convert history to messages
      const msgs = [];
      historyData.reverse().forEach(h => {
        msgs.push({ type: 'user', content: h.user_message, time: h.created_at });
        msgs.push({ type: 'ai', content: h.ai_response, time: h.created_at });
      });
      setMessages(msgs);
    } catch (error) {
      console.error('Error fetching history:', error);
    } finally {
      setLoadingHistory(false);
    }
  };

  const sendMessage = async (text) => {
    const messageText = text || inputMessage;
    if (!messageText.trim()) return;

    const token = localStorage.getItem('token');
    if (!token) {
      toast.error('Lütfen giriş yapın');
      return;
    }

    // Add user message immediately
    setMessages(prev => [...prev, { type: 'user', content: messageText, time: new Date().toISOString() }]);
    setInputMessage('');
    setLoading(true);

    try {
      const response = await axios.post(
        `${API}/ai-coach/chat`,
        { message: messageText },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      setMessages(prev => [...prev, {
        type: 'ai',
        content: response.data.response,
        time: new Date().toISOString()
      }]);

      if (response.data.remaining_messages !== 'unlimited') {
        setRemainingMessages(response.data.remaining_messages);
      }
    } catch (error) {
      if (error.response?.status === 403) {
        toast.error(error.response.data.detail);
        setRemainingMessages(0);
      } else {
        toast.error('AI Koç şu an meşgul, biraz sonra tekrar deneyin');
      }
      // Remove the user message if failed
      setMessages(prev => prev.slice(0, -1));
    } finally {
      setLoading(false);
    }
  };

  const formatTime = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleTimeString('tr-TR', { hour: '2-digit', minute: '2-digit' });
  };

  return (
    <div className="min-h-screen pt-16 bg-gradient-to-b from-indigo-50 to-white flex flex-col">
      <SEO {...SEO_CONFIG.aiCoach} />
      {/* Header */}
      <div className="bg-gradient-to-r from-indigo-600 to-purple-600 text-white px-4 py-6">
        <div className="max-w-2xl mx-auto">
          {/* Back Button */}
          <Button 
            variant="ghost" 
            className="text-white hover:bg-white/20 mb-4"
            onClick={() => navigate(-1)}
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Geri
          </Button>
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 bg-white/20 rounded-full flex items-center justify-center">
              <Sparkles className="h-6 w-6" />
            </div>
            <div>
              <h1 className="text-2xl font-bold">AI Koç</h1>
              <p className="text-indigo-200">Motivasyon & Hedef Asistanın</p>
            </div>
          </div>
        </div>
      </div>

      {/* Messages Area */}
      <div className="flex-1 overflow-y-auto">
        <div className="max-w-2xl mx-auto px-4 py-6 space-y-4">
          {/* Welcome Message */}
          {messages.length === 0 && !loadingHistory && (
            <div className="text-center py-8">
              <div className="w-20 h-20 bg-indigo-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Sparkles className="h-10 w-10 text-indigo-600" />
              </div>
              <h2 className="text-xl font-semibold mb-2">Merhaba! Ben Koç AI 👋</h2>
              <p className="text-gray-600 mb-6">
                Motivasyon, hedef belirleme ve günlük öneriler konusunda sana yardımcı olabilirim.
              </p>

              {/* Quick Prompts */}
              <div className="flex flex-wrap justify-center gap-2">
                {QUICK_PROMPTS.map((prompt, i) => (
                  <Button
                    key={i}
                    variant="outline"
                    className="text-sm"
                    onClick={() => sendMessage(prompt.text)}
                    disabled={loading}
                  >
                    <prompt.icon className="h-4 w-4 mr-2" />
                    {prompt.text}
                  </Button>
                ))}
              </div>
            </div>
          )}

          {/* Messages */}
          {messages.map((msg, index) => (
            <div
              key={index}
              className={`flex ${msg.type === 'user' ? 'justify-end' : 'justify-start'}`}
            >
              <div
                className={`max-w-[80%] px-4 py-3 rounded-2xl ${
                  msg.type === 'user'
                    ? 'bg-indigo-600 text-white rounded-br-md'
                    : 'bg-white text-gray-800 shadow rounded-bl-md'
                }`}
              >
                {msg.type === 'ai' && (
                  <div className="flex items-center gap-2 mb-1 text-indigo-600">
                    <Sparkles className="h-4 w-4" />
                    <span className="text-sm font-medium">Koç AI</span>
                  </div>
                )}
                <p className="whitespace-pre-wrap">{msg.content}</p>
                <p className={`text-xs mt-1 ${msg.type === 'user' ? 'text-indigo-200' : 'text-gray-400'}`}>
                  {formatTime(msg.time)}
                </p>
              </div>
            </div>
          ))}

          {/* Loading */}
          {loading && (
            <div className="flex justify-start">
              <div className="bg-white shadow rounded-2xl px-4 py-3 rounded-bl-md">
                <div className="flex items-center gap-2 text-indigo-600">
                  <Loader2 className="h-4 w-4 animate-spin" />
                  <span className="text-sm">Koç AI düşünüyor...</span>
                </div>
              </div>
            </div>
          )}

          <div ref={messagesEndRef} />
        </div>
      </div>

      {/* Input Area */}
      <div className="border-t bg-white">
        <div className="max-w-2xl mx-auto px-4 py-4">
          {/* Usage Info */}
          {!isPremium && (
            <div className="mb-3 p-3 bg-yellow-50 rounded-lg flex items-center justify-between">
              <div className="flex items-center gap-2 text-yellow-800">
                <Flame className="h-4 w-4" />
                <span className="text-sm">
                  {remainingMessages > 0
                    ? `Bugün ${remainingMessages} mesaj hakkınız kaldı`
                    : 'Günlük mesaj hakkınız doldu'
                  }
                </span>
              </div>
              <Link to="/pricing">
                <Button size="sm" variant="outline" className="text-yellow-800 border-yellow-300">
                  <Crown className="h-4 w-4 mr-1" />
                  Sınırsız
                </Button>
              </Link>
            </div>
          )}

          <div className="flex gap-2">
            <Input
              value={inputMessage}
              onChange={(e) => setInputMessage(e.target.value)}
              placeholder="Koç AI'a bir şey sor..."
              onKeyPress={(e) => e.key === 'Enter' && !loading && sendMessage()}
              disabled={loading || (!isPremium && remainingMessages <= 0)}
              className="flex-1"
            />
            <Button
              onClick={() => sendMessage()}
              disabled={loading || !inputMessage.trim() || (!isPremium && remainingMessages <= 0)}
              className="bg-indigo-600 hover:bg-indigo-700"
            >
              {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AICoachPage;
